import React from "react";
import UserGuideMenu from "./UserGuideMenu";

export default function UserGuide() {
  return (
    <div className="user-guide w-[80%] p-2">
      <div className="title flex justify-between items-center w-full">
        <h2 className=" text-[26px] font-semibold">User Guide for AI Ticket Reply by New Aim</h2>
        <span className="text-[12px] text-[#2F3941] mr-6">Updated on May 2025</span>
      </div>
      <div className="flex w-full p-[20px] leading-[20px]">
        <div className="guide mr-[50px] text-[14px]">
          <ul className="text-[14px]">
            AI Ticket Reply simplifies your customer service workflow by automating responses to common ticket intents.
            Customise intent classification, templates and confidence thresholds to suit your needs. Features include:
            <div className="pl-8 text-[14px]">
              <li className=" list-disc ">Intent-based ticket classification</li>
              <li className=" list-disc">Automated responses using pre-defined templates</li>
              <li className=" list-disc">Customisable confidence thresholds</li>
              <li className=" list-disc">Manual or automated comment reply options for flexibility</li>
            </div>
          </ul>
          <p className="mt-4 ">
            This tool is perfect for teams looking to improve efficiency and reduce response times while maintaining a
            personal touch.
          </p>
          <p className="mt-4 text-blue-800 font-semibold text-lg">
            Try this app today and see your first ticket reply times improve!
          </p>
          <div className="" id="index-0">
            <h5 className="mt-4 font-semibold text-[18px]">How does this app work?</h5>

            <p className="mt-[10px]">
              Once the app is installed and the API is authorized, you can set your preferences in the Configuration
              Centre.
            </p>
            <p className="mt-[10px]">
              Everything is set to default values, where each intent will need to be set up by the user before the
              automated tickets replies will start.
            </p>
          </div>
          <div id="index-1" className="">
            <h5 className="font-semibold text-[18px] mt-4">Intents</h5>

            <p className="mt-[10px]">
              This is how our AI will classify the ticket based on the contents in the first comment from the customer.
              Basically, you can think of this as a category of the ticket.
            </p>
            <div id="index-1-0" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Change Address:{" "}
                <span className="font-normal">If the customer is wanting to change the address on their order</span>
              </h5>
            </div>
            <div id="index-1-1" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Order Cancel: <span className="font-normal">If the customer is requesting to cancel their order </span>
              </h5>
            </div>
            <div id="index-1-2" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Tracking Enquiry:{" "}
                <span className="font-normal">
                  If the customer has any questions regarding the tracking of their order.
                </span>
              </h5>
            </div>
            <div id="index-1-3" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Product Faulty:{" "}
                <span className="font-normal">If the customer has a product issue with their order. </span>
              </h5>
            </div>
            <div id="index-1-4" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Missing Parts:
                <span className="font-normal">The customer has received their whole order but parts are missing.</span>
              </h5>
            </div>
            <div id="index-1-5" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Others:
                <span className="font-normal">
                  When the Zendesk ticket does not fit any other result, will go to Others.
                </span>
              </h5>
            </div>
            <div className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold text-blue-600">
                Note: <span className="font-normal">It is not required to use all intents.</span>
              </h5>
            </div>
          </div>

          <div id="index-2" className="">
            <h5 className="font-semibold text-[18px] mt-4">Setting Up Auto Reply Template</h5>

            <p className="mt-[10px]">
              Before you begin, please note:{" "}
              <span className="font-semibold">All templates are applied across all Zendesk channels</span> (email,
              webform, messaging, etc.), so we recommend keeping the language neutral and applicable to all platforms.
            </p>

            <div className="guide-img mt-4">
              <img src="/images/user_guide_01.png" alt="user-guide" className="w-[90%] mx-auto rounded-lg shadow" />
            </div>

            <p className="mt-2">
              If you click on the three-dot icon to the right of the template you intend to update, you will be
              presented with two options:
            </p>

            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>
                <strong>Edit template:</strong> this allows you to make changes to the template
              </li>
              <li>
                <strong>Reset template:</strong>this allows you to delete all changes to the template (restore to
                default)
              </li>
            </ul>

            <p className="mt-2">
              Click <strong>Edit template</strong> to start customizing.
            </p>

            <div className="guide-img mt-4">
              <img src="/images/user_guide_02.png" alt="user-guide" className="w-[90%] mx-auto rounded-lg shadow" />
            </div>

            <div id="index-2-0" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Additional Tag:{" "}
                <span className="font-normal">
                  This can be customized based on your preference. Make sure to type underscores instead of spaces. One
                  additional tag per intent.
                </span>
              </h5>
            </div>
            <div id="index-2-1" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Tag to Zendesk:{" "}
                <span className="font-normal">
                  This will add the tag automatically when ticket is identified with this intent.
                </span>
              </h5>
            </div>
            <div id="index-2-2" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Allow Auto Reply:{" "}
                <span className="font-normal">
                  You may turn this off and on. However, if you would like the AI to reply on your behalf, this must be
                  turned on. If you only want to use the suggested reply feature, it will display the template within
                  the app in the agent screen.
                </span>
              </h5>
            </div>
            <div className="flex mt-[2px] ml-4">
              <h5 className="text-[14px] font-semibold text-blue-600">
                Note:{" "}
                <span className="font-normal">Activate Template must be turned on for this to be switched on.</span>
              </h5>
            </div>
            <div id="index-2-3" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Confidence Score:{" "}
                <span className="font-normal">
                  The confidence score can be selected in a sliding scale. Every first comment on the ticket will run
                  through our AI and will generate a confidence score. The AI’s confidence score is based on how the
                  comment read relates to the intent. For example, if the confidence threshold is set to 0.8 (80%), only
                  intents with a confidence score of 80% or higher will trigger an automated reply.
                </span>
              </h5>
            </div>
            <div id="index-2-4" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Auto Reply Content:
                <span className="font-normal">
                  This is where you can customize your template for the automated reply that will be triggered by the
                  AI. We recommend that you use a generic template as it will be applied to all channels under your
                  Zendesk instance.{" "}
                </span>
              </h5>
            </div>
            <div id="index-2-5" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Activate Template:
                <span className="font-normal">
                  Turns the template live. If off, the template stays in draft mode. The auto reply content will only be
                  triggered if Allow Auto Reply and Activate Template are turned on and the confidence score meets or
                  exceeds that is set up for the template.
                </span>
              </h5>
            </div>
          </div>

          <p className="mt-[10px]">
            Once the template is set up to your liking, click <span className="font-semibold">Save</span>.
          </p>
          <div className="guide-img mt-4">
            <img src="/images/user_guide_03.png" alt="user-guide" className="w-[90%] mx-auto rounded-lg shadow" />
          </div>
          <p className="mt-[10px]">
            As you can see for the example for the Item Faulty intent, it is now set up. Now you are able to set up
            others as needed!
          </p>

          <div id="index-8">
            <h5 className="font-semibold text-[18px] mt-4">App Errors</h5>
            <div id="index-8-0" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                An error occurred:{" "}
                <span className=" font-normal">
                  This means that the app had an issue. Simply click Refresh to see if the app will reload correctly.
                </span>
              </h5>
            </div>
            <div id="index-8-1" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                No detectable intent:
                <span className="font-normal">
                  The comment does not have context that matches with an existing intent.{" "}
                </span>
              </h5>
            </div>
            <div id="index-8-2" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                If a ticket has already been replied to by the app:
                <span className="font-normal mt-[8px] indent-2">
                  Will provide the template along with the timestamp it was sent.{" "}
                </span>
              </h5>
            </div>
            <div id="index-8-3" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Multiple comments from customer is received:{" "}
                <span className="font-normal mt-[8px] indent-2">
                  The AI Ticket Reply by New Aim can only read the first message in the ticket from the customer. If the
                  customer initial contact is about shipping but before you are able to respond, the next comment is
                  about faulty product. The intent classification will only be based on the first message. So it will
                  show as a tracking_enquiry intent.
                </span>
              </h5>
            </div>
            <div id="index-8-4" className="flex mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                When the app is not authorized or subscription has run out:
                <span className="font-normal mt-[8px] indent-2">
                  The admin will need to either authorize or re-enrol in the subscription in order for the app to work.
                </span>
              </h5>
            </div>
          </div>
          <div id="index-9" className="">
            <h5 className="font-semibold text-[18px] mt-4">FAQ</h5>
            <div id="index-9-0" className=" mt-[10px]">
              <h5 className="text-[14px] font-semibold">What does the AI Ticket Reply by New Aim app do?</h5>
              <p className="font-normal mt-[8px] indent-2">
                It identifies ticket intents and automates first-message responses using pre-set templates.
              </p>
            </div>
            <div id="index-9-1" className=" mt-[10px]">
              <h5 className="text-[14px] font-semibold">How does the app determine the ticket intent?</h5>
              <p className="font-normal mt-[8px] indent-2">
                The ticket intent is identified by our in-house AI, which analyzes the context and content of each
                Zendesk ticket to determine the most relevant intent.
              </p>
            </div>

            <div id="index-9-2" className=" mt-[10px]">
              <h5 className="text-[14px] font-semibold">How to Install AI Ticket Reply by New Aim app?</h5>
              <p className="font-normal mt-[8px] indent-2">Refer to steps in How to Install guide.</p>
            </div>
            <div id="index-9-3" className=" mt-[10px]">
              <h5 className="text-[14px] font-semibold">How to Authorize API Access for AI Ticket Reply by New Aim?</h5>
              <div className="pl-3 mt-2">
                <li className=" list-disc ">Click Authorize API within Authorize API in Configuration Centre</li>
                <li className=" list-disc">
                  A webhook named “Webhook for AI Ticket Reply App by New Aim” will be automatically created
                </li>
                <li className=" list-disc">
                  Then you can set up triggers with custom conditions if required, if something is wrong, deauthorize
                  and authorize again.
                </li>
              </div>
            </div>
            <div id="index-9-4" className=" mt-[10px]">
              <h5 className="text-[14px] font-semibold">How to Uninstall AI Ticket Reply by New Aim app?</h5>
              <div className="pl-3 mt-2">
                <li className=" list-disc ">Click Deauthorize API within Authorize API in Configuration Centre </li>
                <li className=" list-disc">Delete any associated triggers that was for this app.</li>
                <li className=" list-disc">
                  Delete webhook - “Webhook for AI Ticket Reply App by New Aim” needs to be removed manually.
                </li>
              </div>
            </div>
            <div id="index-9-5" className=" mt-[10px]">
              <h5 className="text-[14px] font-semibold">Can I add new intents?</h5>
              <p className="font-normal mt-[8px] indent-2">At this time, users cannot add their own intents. </p>
            </div>
            <div id="index-9-6" className=" mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                Which messages can AI Ticket Reply by New Aim app help me with?
              </h5>
              <p className="font-normal mt-[8px] indent-2">
                Only the first reply that comes in from the customer. Once the intent is established with the first
                message, it cannot re-determine with the next reply from the customer.{" "}
              </p>
            </div>
            <div id="index-9-7" className=" mt-[10px]">
              <h5 className="text-[14px] font-semibold">
                What happens if the customer sends multiple replies within a very short amount of time?
              </h5>
              <p className="font-normal mt-[8px] indent-2">Our app will only read the first reply from the customer.</p>
            </div>
            <div id="" className=" mt-[25px]">
              <p className="font-normal mt-[8px] indent-2">
                If further assistance is required, feel free to contact us directly so that we can assist.
              </p>
            </div>
          </div>
        </div>
        <div className="guide-menu w-[400px] pt-5" style={{ borderColor: "#D8DCDE", borderLeftWidth: "1px" }}>
          <UserGuideMenu />
        </div>
      </div>
    </div>
  );
}
